import type { CSSProperties, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import FadeIn from '@/components/generals/fade-in'
import LineReveal from '@/components/generals/line-reveal'
import TransitionLink from '@/components/generals/transition-link'
import Sparks from '@/components/landing/home/sparks'
import { siteConfig } from '@/constants/site'

const LETTERS: Array<{ char: string, delay: number, accent?: boolean }> = [
    { char: 'L', delay: 0.35 },
    { char: '’', delay: 0.55 },
    { char: 'É', delay: 2.05, accent: true },
    { char: 'l', delay: 0.75 },
    { char: 'i', delay: 0.9 },
    { char: 't', delay: 1.05 },
    { char: 'e', delay: 1.2 }
]

const Hero = (): ReactNode => {
    return (
        <section id='hero' className='relative flex h-svh min-h-[600px] items-center justify-center overflow-hidden bg-ink'>
            <div
                aria-hidden='true'
                className='absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_55%,rgb(42_31_20/0.65),transparent_75%)]'
            />

            <div aria-hidden='true' className='flame'>
                <div className='flame-halo' />
                <div className='flame-floor' />
                <div className='flame-body' />
                <div className='flame-core' />
            </div>

            <Sparks />

            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_50%,transparent_45%,rgb(8_7_6/0.85)_100%)]'
            />

            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-linear-to-b from-transparent to-charcoal'
            />

            <div className='relative z-10 flex flex-col items-center px-6 text-center'>
                <h1 className='wordmark text-[clamp(5rem,21vw,19rem)] leading-[0.95] text-ivory'>
                    <span className='sr-only'>L’Élite</span>

                    <span aria-hidden='true'>
                        {LETTERS.map(letter => (
                            <span
                                key={letter.char}
                                className={cn(letter.accent ? 'wordmark-e wm-e-arrive' : 'wm-letter')}
                                style={{ '--d': `${letter.delay}s` } as CSSProperties}
                            >
                                {letter.char}
                            </span>
                        ))}
                    </span>
                </h1>

                <LineReveal
                    as='p'
                    trigger='mount'
                    delay={2.9}
                    lines={[siteConfig.tagline]}
                    className='mt-4 font-heading text-[clamp(1.5rem,3vw,2.4rem)] italic text-ivory/85 md:mt-6'
                />

                <FadeIn trigger='mount' delay={3.35} className='mt-10 flex flex-col items-center gap-6 sm:flex-row sm:gap-12 md:mt-14'>
                    <TransitionLink href='/menu' className='link-line caps text-ivory/85'>
                        View the menu
                    </TransitionLink>

                    <TransitionLink href='/reserve' className='link-line caps text-ivory/85'>
                        Reserve a table
                    </TransitionLink>
                </FadeIn>
            </div>

            <div className='absolute bottom-8 left-1/2 -translate-x-1/2 md:bottom-10'>
                <FadeIn trigger='mount' delay={3.9} className='flex flex-col items-center gap-3'>
                    <span className='caps text-[0.625rem] text-ivory/45'>Scroll</span>
                    <span aria-hidden='true' className='scroll-cue' />
                </FadeIn>
            </div>
        </section>
    )
}

export default Hero
